import { GridRowParams } from '@mui/x-data-grid-premium';
import React, { useCallback, useState } from 'react';
import { Note, Production } from 'sr-types/lib/search/v1/graphql';
import { ENTITY_CONSTANTS } from '../../common/constants/entityConstants';
import { FEATURE_CONSTANTS } from '../../common/constants/featureConstants';
import ProductionHeaderDataGrid from '../header/ProductionHeaderDataGrid';
import { OpenPanelType, ProductionDataGrid } from '../ProductionDataGrid';
import DataListCellContainer from './DataListCellContainer';
import { DataListCellProvider } from './DataListCellContext';
import ProductionDataListConfiguration from './ProductionDataListConfiguration/ProductionDataListConfiguration';

type ListCellParams = GridRowParams & {
    onEdit: (id: string) => void;
    onNotes: (item: Production) => void;
    onAttachments: (item: Production) => void;
    onTagHandle: (item: Production) => void;
    onCustomAttribute: (item: Production) => void;
    handleSidePanelOpening: (
        row: Production,
        panelId: string,
        note?: Note,
        fromStatusNoteActionClick?: boolean
    ) => void;
    onStatusNoteChanged: (note: Note | null, productionId: string) => void;
    changeDeletedStatusNoteId: (noteId: string | null) => void;
};

export function ProductionDataListDataGrid({
    entity,
    data,
    feature,
    refetch,
    setListLoading,
    setIsEditorOpen,
    onChangeData,
    isConfigurationClicked,
    handleConfigurationState,
    uri,
    controlledSidePanelId,
    onControlledSidePanelClose,
    onDelete,
    setCustomVariables,
    ...rest
}) {
    const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
    const [isNotesPanelOpen, setIsNotesPanelOpen] = useState<boolean>(false);
    const [openPanelType, setOpenPanelType] = useState<OpenPanelType | null>(null);

    const onUpdateSelectedItemId = useCallback((id: string | null) => {
        setSelectedItemId(id);
    }, []);

    const handleNotesPanelToggle = useCallback((isOpen: boolean) => {
        setIsNotesPanelOpen(isOpen);
    }, []);

    const handlePanelTypeChange = useCallback((panelType: OpenPanelType | null) => {
        setOpenPanelType(panelType);
    }, []);

    const ListCell = useCallback(
        (params: ListCellParams) => {
            const {
                row,
                onEdit,
                onNotes,
                onAttachments,
                onTagHandle,
                onCustomAttribute,
                handleSidePanelOpening,
                onStatusNoteChanged,
                changeDeletedStatusNoteId
            } = params;

            return (
                <DataListCellContainer
                    item={row}
                    feature={feature}
                    entity={entity}
                    enableSelectItem
                    selectedItemId={selectedItemId}
                    updatedSelectedItemId={onUpdateSelectedItemId}
                    onNotesPanelToggle={handleNotesPanelToggle}
                    actionHandlers={{
                        onEdit,
                        onDelete,
                        onAttachments,
                        onNotes,
                        onTagHandle,
                        onCustomAttribute
                    }}
                >
                    <ProductionHeaderDataGrid
                        viewState={row}
                        isReadOnly
                        enableSelectItem
                        statusNote={{
                            enableCurrentNote: true,
                            onCurrentNoteClick: handleSidePanelOpening,
                            onStatusNoteDeleted: (noteId, productionId) => {
                                onStatusNoteChanged(null, productionId);
                                changeDeletedStatusNoteId(noteId);
                            },
                            onStatusNoteChanged
                        }}
                    />
                </DataListCellContainer>
            );
        },
        [feature, entity, onDelete, selectedItemId, onUpdateSelectedItemId, handleNotesPanelToggle]
    );

    const listViewConfiguration = {
        enableListView: true,
        rowBufferPx: feature === FEATURE_CONSTANTS.productionWorkspace ? 0 : undefined,
        listViewColDef: {
            field: 'listColumn',
            renderCell: ListCell
        }
    };

    return (
        <DataListCellProvider isNotesPanelOpen={isNotesPanelOpen} openPanelType={openPanelType}>
            <ProductionDataGrid
                entity={entity}
                feature={feature}
                data={data}
                uri={uri}
                history={undefined}
                onChangeData={onChangeData}
                controlledSidePanelId={controlledSidePanelId}
                onControlledSidePanelClose={onControlledSidePanelClose}
                onNotesPanelToggle={handleNotesPanelToggle}
                onPanelTypeChange={handlePanelTypeChange}
                listViewConfiguration={listViewConfiguration}
                {...rest}
            />

            {isConfigurationClicked && (
                <ProductionDataListConfiguration
                    entity={ENTITY_CONSTANTS.workspace}
                    handleConfigurationModalState={handleConfigurationState}
                    setCustomVariables={setCustomVariables}
                />
            )}
        </DataListCellProvider>
    );
}
